"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BookOpen, Trophy, CircleCheck as CheckCircle2, Cpu, Code as Code2, Layers, Lightbulb, FolderKanban, FileCheck2, Target, Cog, Zap, ShieldCheck, Crosshair, Flame } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface PointItem {
  en: string;
  vi: string;
  icon: React.ElementType;
}
const corePoints: PointItem[] = [{
  en: "Build foundations",
  vi: "Xây dựng nền tảng",
  icon: BookOpen
}, {
  en: "Learn mechanisms",
  vi: "Học cơ chế vận hành",
  icon: Cog
}, {
  en: "Learn programming",
  vi: "Học lập trình",
  icon: Code2
}, {
  en: "Understand sensors",
  vi: "Hiểu về cảm biến",
  icon: Cpu
}, {
  en: "Practice problem solving",
  vi: "Rèn luyện giải quyết vấn đề",
  icon: Lightbulb
}, {
  en: "Build projects",
  vi: "Thực hiện dự án",
  icon: FolderKanban
}];
const competitionPoints: PointItem[] = [{
  en: "Work with specific rules",
  vi: "Làm việc theo luật thi đấu",
  icon: FileCheck2
}, {
  en: "Solve mission constraints",
  vi: "Giải quyết giới hạn nhiệm vụ",
  icon: Target
}, {
  en: "Optimize mechanisms",
  vi: "Tối ưu hóa cơ cấu",
  icon: Zap
}, {
  en: "Optimize programming",
  vi: "Tối ưu hóa lập trình",
  icon: Layers
}, {
  en: "Practice reliability",
  vi: "Rèn luyện độ ổn định",
  icon: ShieldCheck
}, {
  en: "Develop strategy",
  vi: "Phát triển chiến thuật",
  icon: Crosshair
}, {
  en: "Simulate competition conditions",
  vi: "Mô phỏng điều kiện thi đấu",
  icon: Flame
}];
export default function Comparison() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getSavedLang = (): "vi" | "en" => {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem("roboto_lang");
        if (saved === "en" || saved === "vi") return saved;
      }
      return "vi";
    };
    setLang(getSavedLang());
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail === "vi" || customEvent.detail === "en") {
        setLang(customEvent.detail);
      } else {
        const saved = localStorage.getItem("roboto_lang");
        if (saved === "en" || saved === "vi") setLang(saved);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const titleText = {
    vi: "ROBOTO học Robotics thế nào — và thi đấu khác gì?",
    en: "How ROBOTO teaches Robotics — and how competition differs"
  };
  const leftTitle = {
    vi: "HỌC NỀN TẢNG",
    en: "CORE LEARNING"
  };
  const rightTitle = {
    vi: "CHUẨN BỊ THI ĐẤU",
    en: "COMPETITION PREPARATION"
  };
  const explanatoryText = {
    vi: "Chuẩn bị thi đấu được phát triển dựa trên nền tảng kiến thức chứ không thay thế việc học nền tảng.",
    en: "Competition preparation builds directly on foundational learning rather than replacing it."
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="corevscompetitioncomparison" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 text-foreground">
      {/* Background Technical Grid Accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_50%,#000_60%,transparent_100%)] opacity-25" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="mx-auto max-w-3xl text-center mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary px-3 py-1 text-xs uppercase tracking-widest font-semibold">
            {lang === "vi" ? "Lộ trình đào tạo" : "Learning Pathway"}
          </Badge>
          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-tight">
            {titleText[lang]}
          </h2>
        </div>

        {/* Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Left Column: Core Learning */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={containerVariants} className="h-full">
            <Card className="h-full bg-card border-border shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 relative overflow-hidden flex flex-col pt-0">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary z-20" />
              
              {/* Supporting Technical Photo / Diagram Header */}
              <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-border bg-slate-950">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/core_learning_foundations.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/core_learning_foundations.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/core_learning_foundations.png-wvc-sizes" alt="Core Learning Foundational Robotics Mechanics" loading="lazy" className="h-full w-full object-cover object-center opacity-90 transition-transform duration-500 hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-900/90 px-2.5 py-1 text-xs font-mono font-semibold text-blue-300 border border-blue-500/30 backdrop-blur-md">
                    <Cpu className="size-3.5 text-blue-400" />
                    {lang === "vi" ? "MÔ HÌNH NỀN TẢNG" : "FOUNDATION DIAGRAM"}
                  </span>
                  <span className="text-[10px] font-mono text-slate-300 tracking-wider uppercase bg-slate-900/80 px-2 py-0.5 rounded border border-slate-700/50">
                    STAGE 01 // CORE LAB
                  </span>
                </div>
              </div>

              <CardHeader className="pb-4 pt-6 px-6 sm:px-8">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                    <BookOpen className="size-6" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                      01 / {lang === "vi" ? "Giai đoạn 1" : "Stage 1"}
                    </span>
                    <CardTitle className="text-2xl font-bold text-card-foreground">
                      {leftTitle[lang]}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="px-6 sm:px-8 pb-8 flex-1">
                <ul className="space-y-3.5 mt-2">
                  {corePoints.map((point, index) => {
                  const IconComponent = point.icon;
                  return <motion.li key={index} data-index={index} variants={itemVariants} className="flex items-start gap-3.5 p-3 rounded-lg bg-muted/50 border border-border/40 hover:bg-muted/80 transition-colors">
                        <div className="p-1.5 rounded-md bg-background text-primary shrink-0 mt-0.5 border border-border/60">
                          <IconComponent className="size-4" />
                        </div>
                        <span className="text-sm font-medium text-foreground leading-snug pt-0.5">
                          {point[lang]}
                        </span>
                      </motion.li>;
                })}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Competition Preparation */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} variants={containerVariants} className="h-full">
            <Card className="h-full bg-card border-border shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-secondary/60 relative overflow-hidden flex flex-col pt-0">
              <div className="absolute top-0 left-0 w-full h-1 bg-secondary z-20" />
              
              {/* Supporting Technical Photo / Diagram Header */}
              <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-border bg-slate-950">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/competition_prep_arena.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/competition_prep_arena.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/competition_prep_arena.png-wvc-sizes" alt="Competition Preparation Arena Robotics Tactical Optimization" loading="lazy" className="h-full w-full object-cover object-center opacity-90 transition-transform duration-500 hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-900/90 px-2.5 py-1 text-xs font-mono font-semibold text-emerald-300 border border-emerald-500/30 backdrop-blur-md">
                    <Trophy className="size-3.5 text-emerald-400" />
                    {lang === "vi" ? "SƠ ĐỒ THI ĐẤU" : "COMPETITION DIAGRAM"}
                  </span>
                  <span className="text-[10px] font-mono text-slate-300 tracking-wider uppercase bg-slate-900/80 px-2 py-0.5 rounded border border-slate-700/50">
                    STAGE 02 // ARENA TAC
                  </span>
                </div>
              </div>

              <CardHeader className="pb-4 pt-6 px-6 sm:px-8">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-secondary/10 text-secondary-foreground shrink-0">
                    <Trophy className="size-6 text-secondary" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                      02 / {lang === "vi" ? "Giai đoạn 2" : "Stage 2"}
                    </span>
                    <CardTitle className="text-2xl font-bold text-card-foreground">
                      {rightTitle[lang]}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="px-6 sm:px-8 pb-8 flex-1">
                <ul className="space-y-3.5 mt-2">
                  {competitionPoints.map((point, index) => {
                  const IconComponent = point.icon;
                  return <motion.li key={index} data-index={index} variants={itemVariants} className="flex items-start gap-3.5 p-3 rounded-lg bg-muted/50 border border-border/40 hover:bg-muted/80 transition-colors">
                        <div className="p-1.5 rounded-md bg-background text-secondary shrink-0 mt-0.5 border border-border/60">
                          <IconComponent className="size-4" />
                        </div>
                        <span className="text-sm font-medium text-foreground leading-snug pt-0.5">
                          {point[lang]}
                        </span>
                      </motion.li>;
                })}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Explanatory Copy */}
        <div className="mt-12 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 p-4 sm:p-5 rounded-xl bg-muted/70 border border-border text-center justify-center shadow-xs">
            <CheckCircle2 className="size-5 text-primary shrink-0 hidden sm:block" />
            <p className="text-sm sm:text-base font-medium text-foreground/90 leading-relaxed">
              {explanatoryText[lang]}
            </p>
          </div>
        </div>
      </div>
    </section>;
}